import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { LOGOMARK } from '@/components/Brand';

/*
 * Loading, said in the app's own words and shapes.
 *
 * Every wait says what it is waiting for - "Checking your sign-in", "Opening
 * Batches", "Loading suppliers" - and looks like the thing that is coming:
 * the brand mark while the app itself starts, the shape of a page while a page
 * opens, table rows while a list loads. A spinner on its own says only that
 * something is happening; this says what, so nobody wonders whether to wait.
 *
 * Nothing flashes. Waits shorter than a beat show nothing at all, and every
 * animation stops for anyone who has asked their system for less motion.
 */

/**
 * True once `ms` has passed. A load that finishes before then never shows a
 * loader: a skeleton that blinks for 80ms reads as a glitch, not as speed.
 */
export function useDelayedFlag(ms: number): boolean {
  const [shown, setShown] = useState(ms <= 0);
  useEffect(() => {
    if (ms <= 0) return;
    const timer = window.setTimeout(() => setShown(true), ms);
    return () => window.clearTimeout(timer);
  }, [ms]);
  return shown;
}

/**
 * The app's own mark, breathing: the logo itself, not a drawing of it, so the
 * wait looks exactly like the icon on the home screen. For the waits that are
 * about the app rather than a list - starting, checking the sign-in.
 */
export function BrandMark({ size = 40, still = false }: { size?: number; still?: boolean }) {
  return (
    <span className={`brand-mark ${still ? '' : 'brand-mark-live'}`} aria-hidden>
      <img src={LOGOMARK} width={size} height={size} alt="" draggable={false} />
    </span>
  );
}

/**
 * A small ring for an inline wait: a table refreshing, a select fetching its
 * options. Takes the text colour, so it sits in a line of text as one more
 * glyph. Antd's own spinners are replaced with this app-wide.
 */
export function Spinner({ size = 16, label }: { size?: number; label?: string }) {
  return (
    <span className="ring-spinner" role={label ? 'status' : undefined}>
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}

/**
 * The whole window, while the app itself is getting ready: starting, checking
 * the sign-in, signing out. The mark, and one line saying which.
 *
 * `index.html` paints the same thing before any script has run, so the first
 * frame and this one are the same picture and the hand-over is invisible.
 */
export function AppSplash({ message, detail }: { message: string; detail?: ReactNode }) {
  return (
    <div className="app-splash" role="status" aria-live="polite">
      <BrandMark size={44} />
      <p className="app-splash-message">{message}</p>
      {detail ? <p className="app-splash-detail">{detail}</p> : null}
    </div>
  );
}

/** Shimmering bars in the shape of what is loading. */
function Bars({ shape, rows }: { shape: 'table' | 'list' | 'detail'; rows: number }) {
  if (shape === 'detail') {
    return (
      <div className="skel-detail" aria-hidden>
        <div className="skel-row">
          <span className="skel skel-w40 skel-h14" />
          <span className="skel skel-w20 skel-h14" />
        </div>
        <div className="skel-grid">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="skel skel-block" />
          ))}
        </div>
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="skel-row">
            <span className="skel skel-w50" />
            <span className="skel skel-w15" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={shape === 'table' ? 'skel-table' : 'skel-list'} aria-hidden>
      {shape === 'table' ? (
        <div className="skel-head">
          <span className="skel skel-w20" />
          <span className="skel skel-w15" />
          <span className="skel skel-w10" />
          <span className="skel skel-w10" />
        </div>
      ) : null}
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel-line">
          <span className="skel-cell">
            <span className="skel skel-w-name" style={{ inlineSize: `${58 - ((i * 13) % 24)}%` }} />
            <span className="skel skel-w-sub" style={{ inlineSize: `${36 - ((i * 7) % 14)}%` }} />
          </span>
          <span className="skel skel-w12" />
          <span className="skel skel-w10" />
          <span className="skel skel-w8" />
        </div>
      ))}
    </div>
  );
}

/**
 * Inside a card, while its data loads: the shape of what is coming, and after
 * a beat a line saying what it is. Used by QueryBoundary.
 *
 * Sized to what is expected, not to a guess. A tab that says "0" loads into an
 * empty state, so it gets an empty-sized space with the words in the middle -
 * not five rows that then collapse into "No new orders". A tab that says 3
 * gets three rows. With nothing to go on, three.
 */
export function ContentLoader({
  label,
  shape = 'table',
  rows,
}: {
  label: string;
  shape?: 'table' | 'list' | 'detail';
  /** How many rows are expected, from a count the page already has. */
  rows?: number;
}) {
  const visible = useDelayedFlag(120);
  const captioned = useDelayedFlag(500);
  if (!visible) return <div className="content-loader-wait" role="status" aria-label={label} />;

  if (rows === 0) {
    return (
      <div className="content-loader content-loader-empty" role="status" aria-live="polite">
        <p className="content-loader-caption is-shown">
          <Spinner size={13} />
          <span>{label}</span>
        </p>
      </div>
    );
  }

  const count = Math.min(Math.max(rows ?? 3, 1), shape === 'detail' ? 4 : 6);
  return (
    <div className="content-loader" role="status" aria-live="polite">
      <Bars shape={shape} rows={count} />
      <p className={`content-loader-caption ${captioned ? 'is-shown' : ''}`}>
        <Spinner size={13} />
        <span>{label}</span>
      </p>
    </div>
  );
}

/**
 * While a page's code arrives - in practice only on a cold start, since
 * navigations keep the current page up until the next is ready. One quiet
 * line, "Opening Batches", and no skeleton: the page brings its own, shaped
 * to its own content, the moment it lands. Two skeletons in a row, a page-
 * shaped one and then the real one, read as the screen loading twice.
 */
export function PageLoader({ label }: { label: string }) {
  const visible = useDelayedFlag(200);
  if (!visible) return <div className="page-loader-wait" role="status" aria-label={label} />;
  return (
    <div className="page-loader" role="status" aria-live="polite">
      <p className="page-loader-caption">
        <Spinner size={14} />
        <span>{label}</span>
      </p>
    </div>
  );
}

/**
 * A thin bar along the top of the window while something is saving, or while
 * a list loads for the first time. Background refreshes are left out on
 * purpose: the counts in the top bar poll every minute, and a bar that moved
 * every minute for no reason anyone asked for would teach people to ignore it.
 */
export function TopProgress() {
  const saving = useIsMutating();
  const firstLoads = useIsFetching({ predicate: (query) => query.state.status === 'pending' });
  const busy = saving + firstLoads > 0;
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!busy) {
      setShown(false);
      return;
    }
    const timer = window.setTimeout(() => setShown(true), 250);
    return () => window.clearTimeout(timer);
  }, [busy]);

  return (
    <div className={`top-progress ${shown ? 'is-busy' : ''}`} aria-hidden>
      <span className="top-progress-bar" />
    </div>
  );
}
